import Decimal from 'decimal.js'
import { DiscountType, VoidCompReasonModel } from 'models'
import { DiscountContractWithCustom } from 'store/models/sellModels'
import { isVoidedItem } from '../orderItemUtil'

/** ceil the calculation discount comp and other that in order level to make the result
 *  over expected and adjust the value in adjustSplitedValue
 */
export function ceil(x: Decimal): Decimal {
    return x.mul(100).ceil().div(100)
}

export function round(x: Decimal): Decimal {
    return (
        x
            //prevent case the js decimal is unexpect like 167.25*0.3 expected result: 50.175, actual: 50.1749999
            .toDecimalPlaces(5)
            .toDecimalPlaces(2)
    )
}

// Example case
// Order with 9.8 subtotal (4 lines with same price 2.45 (no line discount))
// Use entire order discount 10%
// so, $2.45 --> will be discount at 0.245
// but because of rounding 0.245 will become 0.25
// That means sum discount after line rounding will become 1
// but $9.8 with apply 10% discount, should be 0.98
// so we need to adjust, basicly reduce discount in line(1) from 0.25 to 0.23, the rest line still keep 0.25
// WARNING: This function logic is the same one using in backend calculation defined in AdjustSplitedAmountDifferent. Please review the impact before changing the code
export const adjustSplitedValue = (
    splitedValues: Decimal[],
    expectedValue: Decimal,
) => {
    const sumDiscount = splitedValues.reduce(
        (acc, cur) => acc.plus(cur),
        new Decimal(0),
    )

    if (sumDiscount.equals(expectedValue)) {
        return
    }
    // diff at here is always negative number (expectedDiscount < sumDiscount, aka. Over Discount caused by rounding)
    let diff = expectedValue.sub(sumDiscount)

    diff = splitEvenly(diff, splitedValues)

    for (let i2 = 0; i2 < splitedValues.length; i2++) {
        if (splitedValues[i2].plus(diff).greaterThan(0)) {
            splitedValues[i2] = splitedValues[i2].plus(diff)
            diff = new Decimal(0)
        } else {
            // so, we use plus sign to make diff converge to zero
            diff = diff.plus(splitedValues[i2])
            splitedValues[i2] = new Decimal(0)
        }
        if (diff.equals(0)) break
    }
}
export interface BaseOrderItem {
    /** usage quantity of product in the order. normally it will equal line amount
     *  For combo, it will effect to modifier option */
    amount: number
    /** price/priceIncludeAddprice(mod,variant) of item per unit */
    lineTotal: number
    discounts?: DiscountContractWithCustom[]
    voidComp?: VoidCompReasonModel

    /** lineTotal* amount */
    subtotal: Decimal
    /** sum of  discount item level */
    itemDiscountAmount?: number
    /** subtotal - itemDiscountAmount */
    lineTotalWithItemDiscount?: number
    /** line comp discount */
    lineCompDiscount?: number
}

export const calculateLineSubtotal = (item: BaseOrderItem) => {
    return isVoidedItem(item)
        ? new Decimal(0)
        : new Decimal(item.lineTotal ?? 0).mul(item.amount ?? 1)
}
export const sumLineSubtotal = (items: BaseOrderItem[]) =>
    items.reduce(
        (prev, cur) => prev.plus(calculateLineSubtotal(cur)),
        new Decimal(0),
    )
export const sumItemAmount = (items: BaseOrderItem[]): number =>
    items.reduce((prev, cur) => prev + cur.amount, 0)

export const sumTotalDiscount = (orderItem: BaseOrderItem) => {
    if (orderItem.discounts) {
        // sum of total discount value
        return orderItem.discounts.reduce((prev, discount) => {
            let result = new Decimal(discount.value ?? 0).mul(orderItem.amount)

            // sum percentage discount
            if (discount.type === DiscountType.Percentage) {
                result = result
                    .div(100) // divide
                    .mul(orderItem.lineTotal)
            }

            // sum another discount type
            return result.plus(prev)
        }, new Decimal(0))
    } else {
        return new Decimal(0)
    }
}

/**
 * split remaining diff from above evenly splitting
 * for example, diff = 0.02 and numLine = 4,
 * the splitEvenly will be 0
 * this below function will try to adjust the remaining 0.02 to each lines
 */
function splitEvenly(diff: Decimal, splitedValues: Decimal[]) {
    // Math.Floor(diff * 100 / lines.Count) * 0.01m;
    const lineCount = splitedValues.filter((a) => a.greaterThan(0)).length
    //should be absolute number before rounding since if -0.02, we expect 0 but the result is -1
    const splitEvenly = diff
        .abs()
        .mul(100)
        .div(lineCount)
        .floor()
        .div(100)
        .mul(diff.lessThan(0) ? -1 : 1)

    for (let i = 0; i < splitedValues.length; i++) {
        if (splitedValues[i].plus(splitEvenly).greaterThan(0)) {
            splitedValues[i] = splitedValues[i].plus(splitEvenly)
            diff = diff.minus(splitEvenly)
        }
    }
    return diff
}
